// "Match config" QR codes for The floor is lava (v2) + one-shot match state.
//
// An OLD QR ({gameId, gameType, gameName, roomName}) keeps the classic flow.
// A NEW lava QR is marked with `kind: "lava"` and is self-contained: it
// carries the floor connection (url + token) and the level of every round,
// in order — scanning it fully configures the phone (no ⚙ typing, no unlock
// code needed) and locks the referee to exactly those levels:
//
//   { "kind": "lava", "v": 2,
//     "gameId": 6, "gameName": "The floor is lava", "gameType": "Best_of_5",
//     "url": "https://floor-pc.tailxxxx.ts.net", "token": "k7mp3xw9q2rt5vhn",
//     "levels": ["Rope", "Snake", "Criss Cross", "Love", "Jump"] }
//
// Round N plays levels[N-1] (wrapping if the match is longer than the list).

import { setFloorToken, setFloorUrl } from "./api/floorApi";

export type LavaMatchConfig = {
  kind: "lava";
  v?: number;
  gameId: number;
  gameName?: string;
  roomName?: string;
  gameType: string; // "Best_of_1" | "Best_of_3" | "Best_of_5"
  url: string;
  token?: string;
  levels: string[];
};

export const parseLavaQr = (raw: string): LavaMatchConfig | null => {
  try {
    const d = JSON.parse(raw);
    if (
      d &&
      d.kind === "lava" &&
      typeof d.url === "string" &&
      d.gameId != null &&
      Array.isArray(d.levels) &&
      d.levels.length > 0
    )
      return d as LavaMatchConfig;
  } catch {}
  return null;
};

// scanning the QR IS the configuration: store the floor address + token
export const applyLavaFloorConfig = (cfg: LavaMatchConfig) => {
  setFloorUrl(cfg.url);
  if (cfg.token != null) setFloorToken(cfg.token);
};

// ---- one-shot match state --------------------------------------------------
// Persisted per device so a finished round (or match) can never be replayed:
// closing the page, pressing back or re-scanning the QR resumes — it does
// not restart.

export type LavaMatchState = {
  round: number;
  scoreStatus: { yellowTeam: number; greenTeam: number };
  times: { yellow: number | null; green: number | null };
  done?: boolean;
  winner?: "YELLOW" | "GREEN" | null;
};

const stateKey = (gameId: number | string) =>
  `lavaMatch-${localStorage.getItem("uuid") || "nouser"}-${gameId}`;

export const loadLavaMatch = (
  gameId: number | string
): LavaMatchState | null => {
  try {
    const raw = localStorage.getItem(stateKey(gameId));
    return raw ? (JSON.parse(raw) as LavaMatchState) : null;
  } catch {
    return null;
  }
};

export const saveLavaMatch = (gameId: number | string, s: LavaMatchState) => {
  try {
    localStorage.setItem(stateKey(gameId), JSON.stringify(s));
  } catch {}
};

export const isLavaMatchDone = (gameId: number | string) =>
  !!loadLavaMatch(gameId)?.done;
